
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  defaultOptions,
  checkStatus,
  parseJSON,
  getLocationOrigin,
} from '../../../../services/fetchTools';
import { withRouter } from 'react-router';
import {
  colors,
  shadows,
  card,
  table,
  btn,
  btnGroup,
  empty,
  spinner as spinnerStyle,
} from '../../../../style/common/partnerStyle';

var Spinner = require('react-spinkit');

// ─── Local micro-styles ────────────────────────────────────────────────────────

const S = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: '0',
    boxSizing: 'border-box',
  },

  // Status badge: Yes / No
  badge: (completed) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    background: completed ? colors.successLt : colors.dangerLt,
    color: completed ? colors.success : colors.danger,
  }),

  // Match-level chip (Final / Semi-Final / League …)
  levelChip: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    background: colors.brandLt,
    color: colors.brand,
  },

  // Buddy avatar row
  buddyRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  buddyTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: '#f3f4f6',
    color: colors.muted,
  },

  // Action icon buttons
  actionCell: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  iconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.15s',
    textDecoration: 'none',
    background: 'transparent',
  },
  iconBtnEdit: {
    color: colors.brand,
    background: colors.brandLt,
  },
  iconBtnDelete: {
    color: colors.danger,
    background: colors.dangerLt,
  },

  // Team names side-by-side
  matchup: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    whiteSpace: 'nowrap',
  },
  vs: {
    fontSize: 10,
    fontWeight: 700,
    color: colors.muted,
    letterSpacing: '0.05em',
    padding: '2px 6px',
    background: '#f3f4f6',
    borderRadius: 4,
  },
};

// ─── SVG icons (inline, no external dep) ──────────────────────────────────────

const EditIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L4.667 14H2v-2.667L11.333 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.333 3.667 12.333 6.667" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const DeleteIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4h12M5.333 4V2.667h5.334V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333h8L12.667 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3.333" width="12" height="11.333" rx="1.333" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M5.333 2v2.667M10.667 2v2.667M2 6.667h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

class ViewCompetitionMatches extends PureComponent {
  static propTypes = {
    competition_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    noOfMatches: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      competition_id: props.competition_id,
      tournament_id: props.id,
      match_list: [],
      is_loading: false,
      fetch_error: null,
    };
    this.fetchMatches = this.fetchMatches.bind(this);
  }

  componentDidMount() {
    this.setState({ is_loading: true });
    this.fetchMatches();
  }

  fetchMatches() {
    const url = `${getLocationOrigin()}/tournament/get_competition_matches/`;
    const options = { ...defaultOptions };
    options['method'] = 'post';
    options['headers'] = { 'content-type': 'application/json' };
    options['body'] = JSON.stringify({ competition_id: this.state.competition_id });

    return fetch(url, options)
      .then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        const list = data.match_list || [];
        const match_type = {
          knockout: list.filter((m) => m.match_level === 'Final').length,
          league:   list.filter((m) => m.league_round > 0).length,
          fetch_complete: true,
        };
        this.setState({ match_list: list, is_loading: false }, () => {
          if (this.props.noOfMatches) this.props.noOfMatches(match_type);
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ is_loading: false, fetch_error: error.message });
      });
  }

  // ─── Render helpers ──────────────────────────────────────────────────────────

  renderSpinner() {
    return (
      <div style={spinnerStyle.wrap}>
        <Spinner name="line-scale-pulse-out-rapid" color={spinnerStyle.color} />
      </div>
    );
  }

  renderEmpty() {
    return (
      <div style={empty.wrap}>
        <div style={empty.icon}>🏟️</div>
        <div style={empty.title}>No matches scheduled</div>
        <p style={empty.text}>
          Matches for this competition will appear here once added.
        </p>
      </div>
    );
  }

  renderError() {
    return (
      <div style={{ ...empty.wrap, color: colors.danger }}>
        <div style={empty.icon}>⚠️</div>
        <div style={{ ...empty.title, color: colors.danger }}>Failed to load matches</div>
        <p style={empty.text}>{this.state.fetch_error}</p>
        <button
          style={{ ...btn.outline, marginTop: 12 }}
          onClick={() => { this.setState({ is_loading: true, fetch_error: null }); this.fetchMatches(); }}
        >
          Retry
        </button>
      </div>
    );
  }

  renderTable() {
    const { match_list } = this.state;

    return (
      <div style={table.wrap}>
        <table style={table.table}>
          <thead>
            <tr>
              <th style={table.th}>Date &amp; Time</th>
              <th style={table.th}>Matchup</th>
              <th style={table.th}>Match Level</th>
              <th style={table.th}>Status</th>
              <th style={table.th}>Coordinator</th>
              <th style={{ ...table.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {match_list.map((match, index) => (
              <tr key={match.fixture_id || index} style={index % 2 === 1 ? table.trAlt : {}}>

                {/* Date & Time */}
                <td style={table.td}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: colors.muted, fontSize: 12 }}>
                    <CalendarIcon />
                    {match.session_start || '—'}
                  </span>
                </td>

                {/* Matchup: Team One vs Team Two */}
                <td style={table.td}>
                  <div style={S.matchup}>
                    <span>{match.team_one.name || '—'}</span>
                    <span style={S.vs}>VS</span>
                    <span>{match.team_two.name || '—'}</span>
                  </div>
                </td>

                {/* Match Level chip */}
                <td style={table.td}>
                  <span style={S.levelChip}>{match.match_level || '—'}</span>
                </td>

                {/* Completed badge */}
                <td style={table.td}>
                  <span style={S.badge(match.is_completed)}>
                    {match.is_completed ? '✓  Completed' : '○  Pending'}
                  </span>
                </td>

                {/* Coordinator / buddy list */}
                <td style={table.td}>
                  <div style={S.buddyRow}>
                    {match.buddy && match.buddy.length > 0
                      ? match.buddy.map((b, i) => (
                          <span key={i} style={S.buddyTag}>
                            {b.name}
                          </span>
                        ))
                      : <span style={{ color: colors.muted, fontSize: 12 }}>—</span>
                    }
                  </div>
                </td>

                {/* Actions */}
                <td style={{ ...table.td, textAlign: 'right' }}>
                  <div style={{ ...S.actionCell, justifyContent: 'flex-end' }}>
                    <a
                      href={`../../#/crud/editMatch/${match.fixture_id}`}
                      title="Edit match"
                      style={{ ...S.iconBtn, ...S.iconBtnEdit }}
                    >
                      <EditIcon />
                    </a>
                    <a
                      href={`../../#/crud/deleteMatch/${match.id}`}
                      title="Delete match"
                      style={{ ...S.iconBtn, ...S.iconBtnDelete }}
                    >
                      <DeleteIcon />
                    </a>
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { is_loading, fetch_error, match_list } = this.state;
    const matchCount = match_list.length;

    return (
      <div style={{  }}>
        <div style={card.wrap}>

          {/* Card header */}
          <div style={card.header}>
            <h2 style={card.title}>
              Match Schedule
              {!is_loading && matchCount > 0 && (
                <span style={card.titleCount}>{matchCount} match{matchCount !== 1 ? 'es' : ''}</span>
              )}
            </h2>
          </div>

          {/* Card body */}
          <div style={{ padding: 0 }}>
            {is_loading && this.renderSpinner()}
            {!is_loading && fetch_error && this.renderError()}
            {!is_loading && !fetch_error && matchCount === 0 && this.renderEmpty()}
            {!is_loading && !fetch_error && matchCount > 0 && this.renderTable()}
          </div>

        </div>
      </div>
    );
  }
}

export default ViewCompetitionMatches;


// WEBPACK FOOTER //
// ./src/app/views/partner/crud/viewCompetitionMatches/index.js